import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
    BarChart3, Calendar, Download, Send, Loader2, RefreshCw,
    FileText, Users, TrendingUp, Banknote, Clock, AlertTriangle,
    CheckCircle2, Eye, Mail, History, Zap, HardHat
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PERIOD_OPTIONS = [
    { value: 'weekly', label: 'Hebdomadaire', icon: Calendar },
    { value: 'monthly', label: 'Mensuel', icon: Calendar },
    { value: 'quarterly', label: 'Trimestriel', icon: Calendar },
    { value: 'semi_annual', label: 'Semestriel', icon: Calendar },
    { value: 'annual', label: 'Annuel', icon: Calendar },
];

const REPORT_RECIPIENTS = [
    'jeremy@valnexo.fr',
    'laurent@valnexo.fr',
    'morgan@valnexo.fr'
];

export default function Reports() {
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [period, setPeriod] = useState('weekly');
    const [reportData, setReportData] = useState(null);
    const [htmlContent, setHtmlContent] = useState('');
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [activeTab, setActiveTab] = useState('generate');

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await api.get('/reports/history?limit=20');
            setHistory(res.data.reports || []);
        } catch (error) {
            console.error('Error loading history:', error);
        } finally {
            setLoadingHistory(false);
        }
    };

    const generateReport = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/reports/synthese?period=${period}`);
            setReportData(res.data.report);
            setHtmlContent(res.data.html_content);
            toast.success('Rapport généré');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur génération rapport');
        } finally {
            setLoading(false);
        }
    };

    const sendReport = async () => {
        setSending(true);
        try {
            await api.post('/reports/synthese/send', { period, send_email: true });
            toast.success(`Rapport envoyé à ${REPORT_RECIPIENTS.join(', ')}`);
            loadHistory();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur envoi rapport');
        } finally {
            setSending(false);
        }
    };

    const viewHistoryReport = async (reportId) => {
        setLoading(true);
        try {
            const res = await api.get(`/reports/history/${reportId}`);
            setReportData(res.data.report_data);
            setHtmlContent(res.data.html_content);
            setActiveTab('generate');
        } catch (error) {
            toast.error('Erreur chargement rapport');
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
    };

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
                            <BarChart3 className="w-7 h-7 text-blue-600" />
                            Synthèse de gestion Valnexo
                        </h1>
                        <p className="text-slate-500 mt-1">
                            Rapports automatiques : hebdo, mensuel, trimestriel, semestriel, annuel
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-slate-500">
                            <Mail className="w-3 h-3 mr-1" />
                            Destinataires : {REPORT_RECIPIENTS.length}
                        </Badge>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList>
                        <TabsTrigger value="generate" className="gap-2">
                            <FileText className="w-4 h-4" />
                            Générer un rapport
                        </TabsTrigger>
                        <TabsTrigger value="history" className="gap-2">
                            <History className="w-4 h-4" />
                            Historique ({history.length})
                        </TabsTrigger>
                    </TabsList>

                    {/* Tab : Générer un rapport */}
                    <TabsContent value="generate" className="space-y-6">
                        {/* Actions */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg">Paramètres du rapport</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-4">
                                    <div className="flex-1">
                                        <Select value={period} onValueChange={setPeriod}>
                                            <SelectTrigger data-testid="period-select">
                                                <SelectValue placeholder="Choisir une période" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {PERIOD_OPTIONS.map(opt => (
                                                    <SelectItem key={opt.value} value={opt.value}>
                                                        <div className="flex items-center gap-2">
                                                            <opt.icon className="w-4 h-4" />
                                                            {opt.label}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button onClick={generateReport} disabled={loading} data-testid="generate-btn">
                                        {loading ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <RefreshCw className="w-4 h-4 mr-2" />
                                        )}
                                        Générer
                                    </Button>
                                    <Button onClick={sendReport} disabled={sending || !reportData} variant="default" className="bg-green-600 hover:bg-green-700" data-testid="send-btn">
                                        {sending ? (
                                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="w-4 h-4 mr-2" />
                                        )}
                                        Envoyer par email
                                    </Button>
                                </div>

                                {/* Destinataires */}
                                <Alert className="mt-4 bg-blue-50 border-blue-200">
                                    <Mail className="h-4 w-4 text-blue-600" />
                                    <AlertTitle className="text-blue-700">Destinataires</AlertTitle>
                                    <AlertDescription className="text-blue-600">
                                        {REPORT_RECIPIENTS.join(', ')}
                                    </AlertDescription>
                                </Alert>
                            </CardContent>
                        </Card>

                        {/* Résumé rapide */}
                        {reportData && (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Global */}
                                <Card className="border-slate-200">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm text-slate-600 flex items-center gap-2">
                                            <TrendingUp className="w-4 h-4" />
                                            Total Global
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">Nouveaux prospects</span>
                                            <span className="font-semibold">{reportData.global?.new_prospects || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">En cours</span>
                                            <span className="font-semibold">{reportData.global?.in_progress || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-amber-600">
                                            <span className="text-sm">⚠️ > 2 semaines</span>
                                            <span className="font-semibold">{reportData.global?.stale_over_2_weeks || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-green-600">
                                            <span className="text-sm">✅ Validés</span>
                                            <span className="font-semibold">{reportData.global?.validated || 0}</span>
                                        </div>
                                        <div className="border-t pt-2 mt-2">
                                            <div className="flex justify-between">
                                                <span className="text-sm text-slate-500">Commissions en cours</span>
                                                <span className="font-medium">{formatCurrency(reportData.global?.commissions_pending)}</span>
                                            </div>
                                            <div className="flex justify-between text-green-600">
                                                <span className="text-sm">💰 Gagnées</span>
                                                <span className="font-semibold">{formatCurrency(reportData.global?.commissions_validated)}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Easy Power */}
                                <Card className="border-amber-200 bg-amber-50/50">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm text-amber-700 flex items-center gap-2">
                                            <Zap className="w-4 h-4" />
                                            Easy Power
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">Nouveaux</span>
                                            <span className="font-semibold">{reportData.easy_power?.new_prospects || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">En cours</span>
                                            <span className="font-semibold">{reportData.easy_power?.in_progress || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-green-600">
                                            <span className="text-sm">✅ Validés</span>
                                            <span className="font-semibold">{reportData.easy_power?.validated || 0}</span>
                                        </div>
                                        <div className="border-t pt-2 mt-2">
                                            <div className="flex justify-between text-green-600">
                                                <span className="text-sm">💰 Commissions</span>
                                                <span className="font-semibold">{formatCurrency(reportData.easy_power?.commissions_validated)}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Chantier */}
                                <Card className="border-blue-200 bg-blue-50/50">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm text-blue-700 flex items-center gap-2">
                                            <HardHat className="w-4 h-4" />
                                            Chantiers
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-2">
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">Nouveaux</span>
                                            <span className="font-semibold">{reportData.chantier?.new_prospects || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-sm text-slate-500">En cours</span>
                                            <span className="font-semibold">{reportData.chantier?.in_progress || 0}</span>
                                        </div>
                                        <div className="flex justify-between text-green-600">
                                            <span className="text-sm">✅ Validés</span>
                                            <span className="font-semibold">{reportData.chantier?.validated || 0}</span>
                                        </div>
                                        <div className="border-t pt-2 mt-2">
                                            <div className="flex justify-between text-green-600">
                                                <span className="text-sm">💰 Commissions</span>
                                                <span className="font-semibold">{formatCurrency(reportData.chantier?.commissions_validated)}</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        )}

                        {/* Aperçu HTML */}
                        {htmlContent && (
                            <Card>
                                <CardHeader className="pb-3">
                                    <div className="flex items-center justify-between">
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <Eye className="w-5 h-5" />
                                            Aperçu du rapport
                                        </CardTitle>
                                        <Button variant="outline" size="sm" onClick={() => {
                                            const blob = new Blob([htmlContent], { type: 'text/html' });
                                            const url = URL.createObjectURL(blob);
                                            const a = document.createElement('a');
                                            a.href = url;
                                            a.download = `rapport-valnexo-${period}.html`;
                                            a.click();
                                        }}>
                                            <Download className="w-4 h-4 mr-2" />
                                            Télécharger HTML
                                        </Button>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div 
                                        className="border rounded-lg overflow-hidden"
                                        style={{ maxHeight: '600px', overflow: 'auto' }}
                                    >
                                        <iframe
                                            srcDoc={htmlContent}
                                            className="w-full h-[600px] border-0"
                                            title="Aperçu rapport"
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* Tab : Historique */}
                    <TabsContent value="history" className="space-y-4">
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-lg">Historique des rapports envoyés</CardTitle>
                                <CardDescription>
                                    Consultez les rapports précédemment générés et envoyés
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loadingHistory ? (
                                    <div className="text-center py-8">
                                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                                    </div>
                                ) : history.length === 0 ? (
                                    <div className="text-center py-8">
                                        <FileText className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                                        <p className="text-slate-500">Aucun rapport envoyé</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {history.map(report => (
                                            <div 
                                                key={report.id}
                                                className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                                                        <FileText className="w-5 h-5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{report.period_label}</p>
                                                        <p className="text-sm text-slate-500">
                                                            {format(new Date(report.sent_at), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Badge variant="outline" className="text-green-600">
                                                        <CheckCircle2 className="w-3 h-3 mr-1" />
                                                        Envoyé
                                                    </Badge>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm"
                                                        onClick={() => viewHistoryReport(report.id)}
                                                    >
                                                        <Eye className="w-4 h-4 mr-1" />
                                                        Voir
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardLayout>
    );
}
